import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import StickyMobileCTA from '@/components/StickyMobileCTA';
import SEOHead from '@/components/SEOHead';
import FAQSection from '@/components/sections/FAQSection';
import { Button } from '@/components/ui/button';
import { ArrowRight, CheckCircle, XCircle, Users, BarChart3, MessageSquare, Calendar, Target, TrendingUp, Clock, Shield, Heart, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import socialMediaImage from '@/assets/shooting-action.jpg';
import teamImage from '@/assets/team-shooting-1.jpg';

const ServiceSocialMedia = () => {
  const painPoints = [
    { problem: "You're posting randomly and hoping something sticks", solution: "We build a data-driven content calendar aligned with your business goals" },
    { problem: "You don't have time to create content AND run your business", solution: "We handle everything—strategy, creation, posting, and engagement" },
    { problem: "You've tried agencies before and felt like just another account number", solution: "You get a dedicated team who knows your name, your brand, and your vision" },
    { problem: "You're not sure what's working or what to post next", solution: "Monthly analytics reports with clear insights and actionable next steps" },
  ];

  const included = [
    { icon: Target, title: 'Custom Brand Strategy', desc: 'A tailored roadmap built around your goals, audience, and industry—not a cookie-cutter template.' },
    { icon: Calendar, title: 'Content Calendar & Scheduling', desc: 'Strategic posting schedules optimized for when your audience is most active.' },
    { icon: MessageSquare, title: 'Community Management', desc: 'We respond to comments, DMs, and engage with your audience like a real human—because we are.' },
    { icon: BarChart3, title: 'Analytics & Reporting', desc: 'Monthly deep-dives into what\'s working, what\'s not, and exactly what we\'re doing about it.' },
    { icon: Zap, title: 'Ad Campaign Management', desc: 'Targeted paid campaigns that put your brand in front of the right people at the right time.' },
    { icon: TrendingUp, title: 'Growth Strategy', desc: 'Proactive audience growth tactics—not just maintaining, but building momentum.' },
  ];

  const bigAgencyProblems = [
    'You\'re client #247 on a spreadsheet',
    'Generic templates reused across industries',
    'Monthly reports you can\'t understand',
    'A new account manager every 3 months',
    'Locked into 12-month contracts',
    'Charging $5K+/mo for cookie-cutter work',
  ];

  const vpmDifference = [
    'A dedicated team that knows your business by heart',
    'Custom strategy built for YOUR industry and audience',
    'Reports you actually understand with real action items',
    'The same faces, every month, invested in your success',
    'Flexible partnerships—we earn your trust, not lock you in',
    'Premium quality at a fraction of the cost',
  ];

  const faqs = [
    { q: "What is social media management?", a: "Social media management is the process of creating, scheduling, analyzing, and engaging with content posted on social platforms like Instagram, Facebook, TikTok, and LinkedIn. It includes strategy development, content creation, community engagement, and performance tracking—all designed to grow your brand and attract clients." },
    { q: "How does social media management work at Vantage Point Media?", a: "We start with a deep-dive discovery call to understand your business, audience, and goals. From there, we build a custom content strategy, create professional content, schedule posts at optimal times, manage community engagement, and deliver monthly performance reports with actionable insights." },
    { q: "Who is social media management for?", a: "Our social media management services are ideal for med spas, dental practices, financial advisors, real estate agents, surgeons, and other professionals who want to grow their online presence but don't have the time or expertise to do it themselves." },
    { q: "How much does social media management cost in Columbus Ohio?", a: "Social media management packages in Columbus typically range from $1,500 to $5,000+ per month depending on scope. Vantage Point Media offers custom packages tailored to your specific needs at a fraction of what large agencies charge. Book a free discovery call for a transparent quote." },
    { q: "What is the best social media agency in Columbus Ohio?", a: "Vantage Point Media is consistently rated as one of the best social media agencies in Columbus, Ohio. We specialize in high-touch, personalized service for professionals—unlike large agencies that treat clients as numbers. Our results speak for themselves: 500+ leads generated and 150-400% increases in profile activity." },
    { q: "How quickly will I see results from social media management?", a: "Most clients see measurable engagement increases within the first 30 days. Significant lead generation typically kicks in by month 2-3 as we optimize your strategy based on real data. Consistency is key—businesses with regular posting see 150-400% more profile activity within 90 days." },
    { q: "Do I need to create any content myself?", a: "Absolutely not. We handle everything from strategy to creation to posting. All we need is occasional access to your business for photos and videos, and we take care of the rest. We can also pair this with our video production services for professional content." },
    { q: "What social media platforms do you manage?", a: "We manage Instagram, Facebook, TikTok, LinkedIn, YouTube, and Google Business Profile. During our discovery call, we'll recommend the platforms that make the most sense for your specific business, audience, and industry." },
    { q: "What if I've been burned by social media agencies before?", a: "We hear this a lot—and it's exactly why we do things differently. No long-term contracts, transparent monthly reporting, and a dedicated team that stays with you. We earn your business every single month through results, not lock-ins." },
    { q: "Can I approve social media posts before they go live?", a: "Of course. We send everything for approval in advance through a simple approval workflow. Most clients trust us enough within the first month to let us handle it—but the option is always there." },
    { q: "What is the best social media strategy for med spas?", a: "The best social media strategy for med spas combines before-and-after content, educational reels about treatments, behind-the-scenes footage, patient testimonials, and targeted ad campaigns. Vantage Point Media has deep experience in med spa marketing and knows exactly what drives bookings." },
    { q: "Do you offer social media management near me in Columbus Ohio?", a: "Yes! Vantage Point Media is based in Central Ohio and serves businesses throughout Columbus, Dublin, Westerville, Worthington, Powell, Hilliard, Grove City, and surrounding areas. We also work with clients across Ohio." },
    { q: "How is social media management different from content strategy?", a: "Social media management focuses on the day-to-day execution—posting, engaging, and growing your social platforms. Content strategy is the higher-level plan that determines what to create, why, and how it all fits together across channels. We offer both as standalone or combined services." },
    { q: "What's the investment for social media management?", a: "Our packages are tailored to your needs—but they're a fraction of what the big agencies charge. We believe in transparent pricing with no hidden fees. Book a discovery call and we'll give you an honest quote with zero pressure." },
  ];

  return (
    <div className="min-h-screen bg-background pb-16 md:pb-0">
      <SEOHead
        title="Social Media Management Columbus Ohio | Best Social Media Agency | Vantage Point Media"
        description="Full-service social media management in Columbus Ohio. Custom strategy, content creation, community management, analytics & ad campaigns for med spas, dentists, financial advisors & professionals. No contracts."
        canonical="https://vantagepointmediagroup.com/services/social-media"
        keywords="social media management Columbus Ohio, social media agency Columbus Ohio, social media company Columbus, social media marketing Columbus Ohio, Instagram management Columbus, Facebook marketing Columbus Ohio, best social media agency Columbus"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Social Media Management Columbus Ohio",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Vantage Point Media",
              "url": "https://vantagepointmediagroup.com",
              "telephone": "+1-614-625-0865",
              "email": "shay@vantagepointmediagroup.com",
              "areaServed": { "@type": "State", "name": "Ohio" }
            },
            "areaServed": { "@type": "State", "name": "Ohio" },
            "description": "Full-service social media management for businesses in Columbus Ohio. Custom strategy, content creation, community management, analytics reporting, and ad campaign management.",
            "url": "https://vantagepointmediagroup.com/services/social-media",
            "serviceType": "Social Media Management",
            "offers": {
              "@type": "Offer",
              "description": "Custom social media management packages for businesses",
              "areaServed": { "@type": "State", "name": "Ohio" }
            }
          }
        ]}
      />
      <Header />

      <main>
        {/* Hero */}
        <section className="relative pt-28 sm:pt-36 pb-16 sm:pb-24 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img src={socialMediaImage} alt="Social media management Columbus Ohio" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground/95 via-foreground/90 to-foreground/75" />
          </div>
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 bg-primary/20 backdrop-blur-sm border border-primary/30 rounded-full px-4 py-1.5 mb-6">
                <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                <span className="text-background text-xs sm:text-sm font-medium">Social Media Management</span>
              </span>
              <h1 className="font-display font-extrabold text-3xl sm:text-5xl lg:text-6xl text-background leading-tight mb-6">
                Best Social Media Management
                <span className="block text-primary mt-2">in Columbus, Ohio</span>
              </h1>
              <p className="text-base sm:text-xl text-background/85 max-w-2xl mb-8 leading-relaxed">
                Your competitors are showing up on social media every single day. The question isn't whether you need a social media strategy—it's how much longer you can afford not to have one. Vantage Point Media is a social media agency that helps med spas, dentists, and professionals grow with custom strategy and content.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button variant="hero" size="xl" className="group" asChild>
                  <a href="/booking">
                    Book Your Free Strategy Call
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Entity Clarity Block */}
        <section className="py-10 sm:py-14 bg-muted/20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                <strong className="text-foreground">Vantage Point Media</strong> is a full-service social media management agency in Columbus, Ohio that helps med spas, dental practices, financial advisors, and professionals build their online presence, attract high-value clients, and generate consistent leads through strategic social media content and management. Businesses are increasingly investing in professional social media management to stay competitive—and our clients see 150-400% increases in profile activity within 90 days.
              </p>
            </div>
          </div>
        </section>

        {/* Pain Points → Solutions */}
        <section className="py-16 sm:py-24 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
              <h2 className="font-display font-bold text-2xl sm:text-4xl text-foreground mb-4">
                Why Do Businesses Need Social Media Management?
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg">
                These are the exact problems our clients had before working with us. Every single one is solvable.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {painPoints.map((item, i) => (
                <div key={i} className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-soft">
                  <div className="flex items-start gap-3 mb-4">
                    <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                    <p className="text-foreground/70 text-sm sm:text-base line-through decoration-destructive/40">{item.problem}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-foreground font-medium text-sm sm:text-base">{item.solution}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-16 sm:py-24 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
              <span className="inline-block text-accent font-semibold text-sm uppercase tracking-wider mb-3">
                What You Get
              </span>
              <h2 className="font-display font-bold text-2xl sm:text-4xl text-foreground mb-4">
                What's Included in Our Social Media Management?
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg">Everything a $10K/mo agency offers—without the $10K price tag.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {included.map((item, i) => (
                <div key={i} className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-soft hover:shadow-medium transition-all duration-300 hover:-translate-y-1">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Big Agency vs VPM */}
        <section className="py-16 sm:py-24 bg-foreground text-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
              <h2 className="font-display font-bold text-2xl sm:text-4xl mb-4">
                Why Choose Vantage Point Media Over Big Agencies?
              </h2>
              <p className="text-background/70 text-base sm:text-lg">
                Large agencies charge premium prices for impersonal service. You deserve better.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto">
              <div className="bg-background/5 border border-background/10 rounded-2xl p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-destructive/20 rounded-xl flex items-center justify-center">
                    <XCircle className="w-5 h-5 text-destructive" />
                  </div>
                  <h3 className="font-display font-bold text-xl">The Big Agencies</h3>
                </div>
                <ul className="space-y-4">
                  {bigAgencyProblems.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <XCircle className="w-4 h-4 text-destructive/70 flex-shrink-0 mt-1" />
                      <span className="text-background/70 text-sm sm:text-base">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-primary/10 border border-primary/20 rounded-2xl p-6 sm:p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-bl-lg">BETTER WAY</div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-primary/20 rounded-xl flex items-center justify-center">
                    <Heart className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-display font-bold text-xl">Vantage Point Media</h3>
                </div>
                <ul className="space-y-4">
                  {vpmDifference.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                      <span className="text-background/90 text-sm sm:text-base">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Trust / Partnership */}
        <section className="py-16 sm:py-24 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row gap-8 sm:gap-12 items-center max-w-6xl mx-auto">
              <div className="w-full lg:w-1/2">
                <img src={teamImage} alt="Vantage Point Media team working with client on social media strategy" className="w-full aspect-[4/3] object-cover rounded-2xl shadow-strong" />
              </div>
              <div className="w-full lg:w-1/2">
                <span className="inline-block text-accent font-semibold text-sm uppercase tracking-wider mb-3">Why We're Different</span>
                <h2 className="font-display font-bold text-2xl sm:text-3xl text-foreground mb-4">
                  We Don't Do Clients.
                  <span className="text-primary block mt-1">We Do Partners.</span>
                </h2>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  The big agencies see you as a number on a spreadsheet. We see you as a partner. Your wins are our wins. When your phone rings with a new lead from a post we created—that's the moment we live for.
                </p>
                <div className="space-y-4">
                  {[
                    { icon: Users, text: 'Dedicated team that knows your business inside and out' },
                    { icon: Clock, text: 'Same-day responses—because your time matters' },
                    { icon: Shield, text: 'No long-term contracts—we earn your trust monthly' },
                    { icon: Heart, text: 'We genuinely care about your growth, not just our invoice' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-4 h-4 text-accent" />
                      </div>
                      <span className="text-foreground text-sm sm:text-base">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Authority: Best Social Media for Industries */}
        <section className="py-16 sm:py-24 bg-muted/20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-display font-bold text-2xl sm:text-3xl text-foreground mb-6 text-center">
                Best Social Media Management for Professional Services
              </h2>
              <div className="space-y-6">
                <article className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-soft">
                  <h3 className="font-display font-semibold text-lg text-foreground mb-3">Best Social Media Agency for Med Spas</h3>
                  <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                    Med spas thrive on visual content—before-and-afters, treatment demos, and patient stories. Vantage Point Media specializes in creating scroll-stopping social media content for med spas in Columbus, Ohio, combining professional <Link to="/services/video-production" className="text-primary hover:underline">video production</Link> with strategic posting to drive treatment bookings and build trust with prospective patients.
                  </p>
                </article>
                <article className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-soft">
                  <h3 className="font-display font-semibold text-lg text-foreground mb-3">Top Social Media Solutions for Dental Practices</h3>
                  <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                    Dental practices that invest in consistent social media presence see significant increases in new patient inquiries. Our <Link to="/services/content-strategy" className="text-primary hover:underline">content strategy</Link> approach for dental practices includes educational content, team spotlights, and patient testimonials that build community trust and drive appointments.
                  </p>
                </article>
                <article className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-soft">
                  <h3 className="font-display font-semibold text-lg text-foreground mb-3">Why Financial Advisors Are Investing in Social Media</h3>
                  <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                    According to industry trends, financial advisors with an active social media presence generate 40% more leads than those without. Businesses are increasingly using social media to build credibility and attract high-net-worth clients. Vantage Point Media helps financial professionals create compliant, engaging content that positions them as trusted authorities.
                  </p>
                </article>
              </div>
            </div>
          </div>
        </section>

        {/* Cross-linking to other services */}
        <section className="py-12 sm:py-16 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="font-display font-bold text-xl sm:text-2xl text-foreground mb-6">
                Explore Our Other Services
              </h2>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/services/video-production" className="bg-card border border-border rounded-xl p-5 shadow-soft hover:shadow-medium transition-all hover:-translate-y-1 flex-1 max-w-xs">
                  <h3 className="font-display font-bold text-foreground mb-1">Video Production</h3>
                  <p className="text-muted-foreground text-sm">Cinematic brand videos that captivate and convert</p>
                </Link>
                <Link to="/services/content-strategy" className="bg-card border border-border rounded-xl p-5 shadow-soft hover:shadow-medium transition-all hover:-translate-y-1 flex-1 max-w-xs">
                  <h3 className="font-display font-bold text-foreground mb-1">Content Strategy</h3>
                  <p className="text-muted-foreground text-sm">Data-driven plans that turn content into leads</p>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FAQSection
          faqs={faqs}
          heading="Social Media Management: Questions & Answers"
          subheading="Everything you need to know about working with a social media agency in Columbus, Ohio."
          pageUrl="https://vantagepointmediagroup.com/services/social-media"
        />

        {/* Final CTA */}
        <section className="py-16 sm:py-24 bg-foreground relative overflow-hidden">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/10 to-transparent" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display font-bold text-2xl sm:text-4xl text-background mb-4">
              Your Competitors Are Already Showing Up.
              <span className="text-primary block mt-2">Are You?</span>
            </h2>
            <p className="text-background/80 text-base sm:text-lg max-w-2xl mx-auto mb-8">
              Every day without a strategy is a day your competitors are winning clients that should be yours. Let's change that—starting with a free, no-pressure discovery call.
            </p>
            <Button variant="hero" size="xl" className="group w-full sm:w-auto" asChild>
              <a href="/booking">
                Book Your Free Discovery Call
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
            <p className="mt-4 text-background/50 text-xs sm:text-sm">No contracts. No pressure. Just a conversation about your growth.</p>
          </div>
        </section>
      </main>

      <Footer />
      <StickyMobileCTA />
    </div>
  );
};

export default ServiceSocialMedia;
